import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Loader2 } from "lucide-react";
import AuthShell from "../../components/AuthShell";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { maskEmail } from "../../context/AuthContext";

const LEN = 6;

export default function AdminVerify2FA() {
  const { pendingAdminEmail, verify2FA, resend2FA } = useAdminAuth();
  const navigate = useNavigate();
  const [digits, setDigits] = useState(Array(LEN).fill(""));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendState, setResendState] = useState("idle"); // idle | sending | sent
  const inputsRef = useRef([]);

  useEffect(() => {
    if (!pendingAdminEmail) {
      navigate("/signin", { replace: true });
    }
  }, [pendingAdminEmail, navigate]);

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  const setAt = (idx, value) => {
    setDigits((prev) => {
      const next = [...prev];
      next[idx] = value;
      return next;
    });
  };

  const onChange = (idx) => (e) => {
    const raw = e.target.value.replace(/\D/g, "");
    if (!raw) {
      setAt(idx, "");
      return;
    }
    if (raw.length > 1) {
      const chars = raw.slice(0, LEN - idx).split("");
      setDigits((prev) => {
        const next = [...prev];
        chars.forEach((c, i) => (next[idx + i] = c));
        return next;
      });
      const nextIdx = Math.min(idx + chars.length, LEN - 1);
      inputsRef.current[nextIdx]?.focus();
      return;
    }
    setAt(idx, raw);
    if (idx < LEN - 1) inputsRef.current[idx + 1]?.focus();
  };

  const onKeyDown = (idx) => (e) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
  };

  const onPaste = (e) => {
    const text = (e.clipboardData.getData("text") || "").replace(/\D/g, "");
    if (!text) return;
    e.preventDefault();
    const chars = text.slice(0, LEN).split("");
    setDigits(() => {
      const next = Array(LEN).fill("");
      chars.forEach((c, i) => (next[i] = c));
      return next;
    });
    const nextIdx = Math.min(chars.length, LEN - 1);
    inputsRef.current[nextIdx]?.focus();
  };

  const onSubmit = async (e) => {
    e?.preventDefault();
    const code = digits.join("");
    if (code.length !== LEN) {
      setError("Please enter the full 6-digit code");
      return;
    }
    setError("");
    setLoading(true);
    const result = await verify2FA(code);
    setLoading(false);
    if (result.ok) {
      if (result.mustChangePassword) {
        navigate("/admin/change-password", { replace: true });
      } else {
        navigate("/admin/users", { replace: true });
      }
    } else {
      setError(result.error || "Invalid code, please try again");
      setDigits(Array(LEN).fill(""));
      inputsRef.current[0]?.focus();
    }
  };

  const onResend = async () => {
    if (!pendingAdminEmail) return;
    setResendState("sending");
    const result = await resend2FA();
    setResendState(result.ok ? "sent" : "idle");
    if (!result.ok) setError(result.error || "Failed to resend code");
    setTimeout(() => setResendState("idle"), 2500);
  };

  if (!pendingAdminEmail) return null;

  return (
    <AuthShell
      title="Admin verification"
      subtitle={
        <>
          We've sent a 6-digit code to{" "}
          <span className="font-medium text-gray-700">
            {maskEmail(pendingAdminEmail)}
          </span>
        </>
      }
    >
      <form onSubmit={onSubmit}>
        <div className="mb-4 flex items-center gap-2 text-xs text-gray-500">
          <Shield className="h-4 w-4 text-indigo-600" />
          Admin sessions require a fresh code on every sign in
        </div>
        <div
          className="flex items-center justify-between gap-1.5 sm:gap-2"
          onPaste={onPaste}
        >
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (inputsRef.current[i] = el)}
              inputMode="numeric"
              maxLength={LEN}
              value={d}
              onChange={onChange(i)}
              onKeyDown={onKeyDown(i)}
              className="h-10 w-10 rounded-lg border border-gray-300 bg-white text-center text-lg font-semibold text-gray-900 outline-none transition-colors duration-150 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 sm:h-12 sm:w-12"
              aria-label={`Digit ${i + 1}`}
            />
          ))}
        </div>
        {error && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        <button type="submit" className="btn-primary mt-6 w-full" disabled={loading}>
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          Verify code
        </button>
      </form>
      <div className="mt-5 flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={onResend}
          className="font-medium text-indigo-600 hover:text-indigo-700 disabled:opacity-60"
          disabled={resendState !== "idle"}
        >
          {resendState === "idle" && "Resend code"}
          {resendState === "sending" && "Sending…"}
          {resendState === "sent" && "Code sent"}
        </button>
        <button
          type="button"
          onClick={() => navigate("/signin")}
          className="font-medium text-gray-600 hover:text-gray-900"
        >
          Cancel
        </button>
      </div>
    </AuthShell>
  );
}
